import { genres, regions, versions, years } from "@/mocks/movies";

export interface MovieFilters {
  region: string;
  year: string;
  version: string;
  genre: string;
}

export const emptyFilters: MovieFilters = {
  region: "全部",
  year: "全部",
  version: "全部",
  genre: "全部",
};

interface FilterSelectProps {
  label: string;
  icon: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

function FilterSelect({ label, icon, value, options, onChange }: FilterSelectProps) {
  return (
    <label className="flex-1 min-w-0">
      <span className="sr-only">{label}</span>
      <div className="flex items-center gap-2 bg-background-900 border border-background-700 rounded-md px-3 h-11 focus-within:border-accent-600 transition-colors">
        <i className={`${icon} text-foreground-400 text-base shrink-0`} />
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-foreground-100 font-label cursor-pointer appearance-none"
          aria-label={label}
        >
          {options.map((opt) => (
            <option key={opt} value={opt} className="bg-background-900 text-foreground-100">
              {opt === "全部" ? `${label}：全部` : opt}
            </option>
          ))}
        </select>
        <i className="ri-arrow-down-s-line text-foreground-500 text-base shrink-0 pointer-events-none" />
      </div>
    </label>
  );
}

interface MovieFilterBarProps {
  filters: MovieFilters;
  onChange: (next: MovieFilters) => void;
  onReset: () => void;
  resultCount: number;
}

export default function MovieFilterBar({ filters, onChange, onReset, resultCount }: MovieFilterBarProps) {
  const activeCount = Object.values(filters).filter((v) => v !== "全部").length;

  const set = (key: keyof MovieFilters, value: string) =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="bg-background-900/60 border border-background-800 rounded-lg p-4 md:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2 text-foreground-100 font-label text-sm">
          <i className="ri-filter-3-line text-accent-500" />
          筛选条件
          {activeCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-primary-600 text-background-50 text-xs">
              {activeCount}
            </span>
          )}
        </div>
        <div className="sm:ml-auto flex items-center gap-3 text-xs text-foreground-400 font-label">
          <span>共 {resultCount} 部影片</span>
          <button
            type="button"
            onClick={onReset}
            disabled={activeCount === 0}
            className="flex items-center gap-1 px-3 h-8 rounded-md border border-background-700 hover:border-accent-600 hover:text-accent-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重置
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <FilterSelect
          label="上映地区"
          icon="ri-earth-line"
          value={filters.region}
          options={regions}
          onChange={(v) => set("region", v)}
        />
        <FilterSelect
          label="上映年份"
          icon="ri-calendar-line"
          value={filters.year}
          options={years}
          onChange={(v) => set("year", v)}
        />
        <FilterSelect
          label="版本类型"
          icon="ri-disc-line"
          value={filters.version}
          options={versions}
          onChange={(v) => set("version", v)}
        />
        <FilterSelect
          label="影片类型"
          icon="ri-price-tag-3-line"
          value={filters.genre}
          options={genres}
          onChange={(v) => set("genre", v)}
        />
      </div>
    </div>
  );
}